import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { addMonths, endOfISOWeek, startOfISOWeek } from 'date-fns';
type WeekDatePickerProps = {
  /** 선택된 “기준 날짜” (기존 pickedDate와 동일 의미) */
  value: Date;
  /** 날짜 클릭/확정 시 호출. value(=특정일)만 넘긴다. 주 범위는 부모에서 계산 */
  onChange: (picked: Date) => void;
  /** 처음 보여줄 달(옵션) */
  initialMonth?: Date;
};

type Mode = 'calendar' | 'monthYear';

export default function WeekDatePicker({
  value,
  onChange,
  initialMonth,
}: WeekDatePickerProps) {
  const now = new Date();
  const THIS_WEEK_START = startOfISOWeek(now);
  const THIS_WEEK_END = endOfISOWeek(now);

  function isDisabledDate(d: Date) {
    // 이번 주(월요일)부터 미래까지 전부 차단
    return d.getTime() >= THIS_WEEK_START.getTime();
  }

  const MS_DAY = 24 * 60 * 60 * 1000;
  const pickValidDateInMonth = (
    year: number,
    month: number,
    thisWeekStart: Date,
  ) => {
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const lastAllowed = new Date(thisWeekStart.getTime() - MS_DAY); // 이번 주 시작 하루 전
    return lastDayOfMonth.getTime() > lastAllowed.getTime()
      ? lastAllowed
      : lastDayOfMonth;
  };

  // 현재 캘린더가 보여주는 연/월
  const [view, setView] = useState(() => {
    const base = initialMonth ?? value ?? new Date();
    return { year: base.getFullYear(), month: base.getMonth() }; // month: 0~11
  });

  // 월/연 선택 모드 토글
  const [isCalendar, setIsCalendar] = useState(true);

  // 팝업 내부 임시 선택일(적용 버튼 누르기 전)
  const [tempSelected, setTempSelected] = useState<Date>(value ?? new Date());

  // monthYear 모드용 임시 연/월
  const [tmpYear, setTmpYear] = useState(view.year);
  const [tmpMonth, setTmpMonth] = useState(view.month);

  const daysMatrix = useMemo(
    () => buildCalendarMatrix(view.year, view.month),
    [view.year, view.month],
  );

  const selectedWeekStart = startOfISOWeek(tempSelected);
  const selectedWeekEnd = endOfISOWeek(tempSelected);

  const goPrevMonth = () => {
    const next = addMonths(new Date(view.year, view.month, 1), -1);
    setView({ year: next.getFullYear(), month: next.getMonth() });
  };
  const goNextMonth = () => {
    const next = addMonths(new Date(view.year, view.month, 1), +1);
    // 미래 달로 넘길 때 오늘 이후 달은 막고 싶으면 여기서 제어 가능
    setView({ year: next.getFullYear(), month: next.getMonth() });
  };

  const startYear = 2020;
  const endYear = now.getFullYear();

  const years = useMemo(
    () =>
      Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i),
    [endYear],
  );

  return (
    <Container>
      <Header>
        <MonthTitle onClick={() => setIsCalendar(!isCalendar)}>
          {view.year}년 {view.month + 1}월
          <ArrowButton>
            <ArrowIcon
              src="/assets/images/arrow/arrow-right-tertiary.svg"
              $rotate={isCalendar ? 90 : -90}
            />
          </ArrowButton>
        </MonthTitle>
        <NavButtonWrapper>
          <NavButton onClick={goPrevMonth}>
            <ArrowIcon
              src="/assets/images/arrow/arrow-right-tertiary.svg"
              $rotate={180}
            />
          </NavButton>
          <NavButton onClick={goNextMonth}>
            <ArrowIcon src="/assets/images/arrow/arrow-right-tertiary.svg" />
          </NavButton>
        </NavButtonWrapper>
      </Header>

      {isCalendar ? (
        <Grid>
          {daysMatrix.map((week, wi) => (
            <WeekRow
              key={wi}
              $highlight={
                // 해당 주가 선택 주와 겹치면 하이라이트(셀 단위도 별도 처리)
                week.some(
                  (d) => d && isBetween(d, selectedWeekStart, selectedWeekEnd),
                )
              }
            >
              {week.map((d, di) => {
                if (!d) return <Cell key={di} />;

                const isCurMonth =
                  d.getMonth() === view.month && d.getFullYear() === view.year;
                const inSelectedWeek = isBetween(
                  d,
                  selectedWeekStart,
                  selectedWeekEnd,
                );

                const disabled = isDisabledDate(d);

                return (
                  <Cell
                    key={di}
                    $dim={!isCurMonth}
                    $inWeek={inSelectedWeek && !disabled}
                    $disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      setTempSelected(d);
                      onChange(d);
                    }}
                  >
                    <span>{d.getDate()}</span>
                  </Cell>
                );
              })}
            </WeekRow>
          ))}
        </Grid>
      ) : (
        <MonthYearWrap>
          <YearScroller>
            {years.map((y) => (
              <YearItem
                key={y}
                $sel={y === tmpYear}
                onClick={() => {
                  const isThisYear = y === now.getFullYear();
                  let nextMonth = tmpMonth;

                  if (isThisYear && tmpMonth > now.getMonth()) {
                    nextMonth = now.getMonth();
                  }
                  setTmpYear(y);
                  setTmpMonth(nextMonth);
                  setView({ year: y, month: nextMonth });
                  const picked = pickValidDateInMonth(
                    y,
                    nextMonth,
                    THIS_WEEK_START,
                  );
                  setTempSelected(picked);
                  onChange(picked);
                }}
              >
                {y}년
              </YearItem>
            ))}
          </YearScroller>

          <MonthScroller>
            {Array.from({ length: 12 }, (_, m) => {
              const disabled =
                tmpYear > now.getFullYear() ||
                (tmpYear === now.getFullYear() && m > now.getMonth());

              return (
                <MonthItem
                  key={m}
                  disabled={disabled}
                  $sel={m === tmpMonth}
                  onClick={() => {
                    if (disabled) return;

                    setTmpMonth(m);
                    const picked = pickValidDateInMonth(
                      tmpYear,
                      m,
                      THIS_WEEK_START,
                    );
                    setView({ year: tmpYear, month: m });
                    setTempSelected(picked);
                    onChange(picked);
                  }}
                >
                  {`${m + 1}월`}
                </MonthItem>
              );
            })}
          </MonthScroller>
        </MonthYearWrap>
      )}
    </Container>
  );
}

function buildCalendarMatrix(year: number, month0: number) {
  const first = new Date(year, month0, 1);
  const firstDow = first.getDay(); // 0=일..6=토
  const mondayIndex = (firstDow + 6) % 7; // 월=0 보정
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  // 앞 패딩 (이전달은 null로 둠, dim 처리용)
  for (let i = 0; i < mondayIndex; i++) cells.push(null);
  // 본월
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month0, d));
  // 뒤 패딩: 7의 배수까지만
  while (cells.length % 7 !== 0) cells.push(null);

  // 7개씩 자르기
  const weeks: (Date | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  while (weeks.length && weeks[weeks.length - 1].every((d) => d === null)) {
    weeks.pop();
  }
  return weeks;
}

function isBetween(d: Date, s: Date, e: Date) {
  return d.getTime() >= s.getTime() && d.getTime() <= e.getTime();
}

const Container = styled.div`
  display: flex;
  padding: 8px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const MonthTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: #424854;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.32px;
`;

const NavButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const NavButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const ArrowButton = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > img {
    width: 24px;
    height: 24px;
  }
`;

const ArrowIcon = styled.img<{ $rotate?: number }>`
  width: 20px;
  height: 20px;
  transform: rotate(${({ $rotate }) => $rotate || 0}deg);
`;
const Grid = styled.div`
  display: grid;
  gap: 8px;
  align-self: stretch;
`;

const WeekRow = styled.div<{ $highlight?: boolean }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  border-radius: 8px;
  ${(p) =>
    p.$highlight &&
    css`
      background: #e4edfd;
    `}
  padding: 2px 0;
`;

type CellProps = {
  $dim?: boolean;
  $inWeek?: boolean;
  $disabled?: boolean;
};

const Cell = styled.div<CellProps>`
  display: flex;
  padding: 8px 4px 16px 4px;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 8px;

  ${({ $inWeek }) =>
    $inWeek &&
    css`
      font-weight: 500;
      color: #3f6ef1;
    `}

  ${({ $dim }) =>
    $dim &&
    css`
      opacity: 0.4;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none; /* 실제 클릭도 차단 */
      background: transparent;
      color: inherit;
    `}
`;

const MonthYearWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  height: 280px;
`;

const YearScroller = styled.div`
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  &::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
  scrollbar-width: none;
`;

const YearItem = styled.div<{ $sel?: boolean }>`
  padding: 8px 10px;
  text-align: center;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${(p) => (p.$sel ? '#3F6EF1' : '#A7ADB8')};
  position: relative;
  &:before,
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    width: ${(p) => (p.$sel ? '80px' : '0')};
    height: 1px;
    background-color: #a7adb88c;
    transform: translateX(-50%);
  }
  &:before {
    top: 0;
  }
  &:after {
    bottom: 0;
  }
`;

const MonthScroller = styled.div`
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  &::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
  scrollbar-width: none;
`;

const MonthItem = styled.button<{ $sel?: boolean }>`
  border: none;
  outline: none;
  background-color: transparent;

  padding: 8px 10px;
  text-align: center;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  color: ${(p) => (p.$sel ? '#3F6EF1' : '#A7ADB8')};
  position: relative;
  font-size: 14px;
  &:before,
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    width: ${(p) => (p.$sel ? '80px' : '0')};
    height: 1px;
    background-color: #a7adb88c;
    transform: translateX(-50%);
  }
  &:before {
    top: 0;
  }
  &:after {
    bottom: 0;
  }
`;
